//
//  SDKDTGroupTreeNode.ts
//  Supernova SDK
//
//  Created by Jiri Trecak.
//
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Imports
import { TokenGroup } from "../../../../model/groups/SDKTokenGroup";
import { sureOf } from "../../../../utils/CommonUtils";
import sortBy from "lodash/sortBy";
import { v4 as uuidv4 } from "uuid";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tree Node Implementation
export class GroupTreeNode {
    get isGroup() {
        return this.element instanceof TokenGroup;
    }
    get name() {
        return this.element.name;
    }
    // -------------------
    // MARK: - Constructor
    constructor(element, tree) {
        this.element = element;
        this.tree = tree;
        if (this.isGroup) {
            this.children = [];
        }
    }
    // ----------------
    // MARK: - Mutation
    applyDefaultSorting() {
        this.children = sortBy(this.children, (child) => (child.isGroup ? 1 : 0));
    }
    addChildInternal(child, atIndex = -1) {
        if (atIndex === -1) {
            this.children.push(child);
        }
        else {
            this.children.splice(atIndex, 0, child);
        }
        this.applyDefaultSorting();
    }
    populate(childrenMap) {
        var _a;
        const { tree, element } = this;
        const children = (_a = childrenMap.get(element.id)) !== null && _a !== void 0 ? _a : [];
        for (const childElement of children) {
            const child = new GroupTreeNode(childElement, tree);
            this.children.push(child);
            if (childElement instanceof TokenGroup) {
                child.populate(childrenMap);
            }
        }
    }
    createGroupChild(name) {
        return this.addChildGroup(new TokenGroup({
            brandId: this.tree.element.brandId,
            tokenType: this.tree.element.tokenType,
            designSystemVersionId: this.tree.element.designSystemVersionId,
            persistentId: uuidv4(),
            isRoot: false,
            id: undefined,
            meta: {
                name,
                description: "",
            },
            childrenIds: [],
        }));
    }
    addChildGroup(groupElement) {
        const { tree } = this;
        // Only groups can be added as children through this
        if (groupElement instanceof TokenGroup === false) {
            throw new Error(
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            `Can't add ${groupElement.id} as group, is ${groupElement}`);
        }
        // Add child
        const child = new GroupTreeNode(groupElement, tree);
        this.addChildInternal(child);
        return child;
    }
    getOrCreateGroup(path) {
        if (path.length === 0) {
            return this;
        }
        const [name, ...rest] = path;
        let child = this.children.find((c) => c.isGroup && c.element.name === name);
        if (!child) {
            child = this.createGroupChild(sureOf(name));
        }
        return child.getOrCreateGroup(rest);
    }
    // ----------------
    // MARK: - Iterators
    forEach(callback, leafsFirst = true) {
        this.forEachInternal(callback, [], leafsFirst);
    }
    forEachInternal(callback, path, leafsFirst) {
        if (!leafsFirst) {
            callback(this, path);
        }
        const newPath = path.concat([this]);
        for (const c of this.children) {
            if (c.isGroup) {
                c.forEachInternal(callback, newPath, leafsFirst);
            }
        }
        if (leafsFirst) {
            callback(this, path);
        }
    }
}
